import { MessagesSquare } from "@tamagui/lucide-icons";
import { useEffect, useRef } from "react";
import { TextInput } from "react-native";
import Animated, {
  type AnimatedRef,
  FadeIn,
  FadeOut,
  scrollTo,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { XStack, getTokenValue } from "tamagui";

import { Button } from "@/components/Button";

const STICKY_BUTTON_Z_INDEX = 10;

interface StickyChatButtonProps {
  scrollRef: AnimatedRef<Animated.ScrollView>;
  secondSectionRef: React.RefObject<any>;
  scrollY?: number;
  chatInputRef?: React.RefObject<TextInput | null>;
}

export default function StickyChatButton({
  scrollRef,
  secondSectionRef,
  scrollY = 0,
  chatInputRef,
}: StickyChatButtonProps) {
  const opacity = useSharedValue(0);
  const secondSectionY = useRef<number>(0);
  const secondSectionHeight = useRef<number>(0);
  const hasScrolled = useRef<boolean>(false);
  const hasReachedThreshold = useRef<boolean>(false);

  // Measure second section position
  useEffect(() => {
    const measureSecondSection = () => {
      if (secondSectionRef.current && scrollRef.current) {
        secondSectionRef.current.measureLayout(
          scrollRef.current as any,
          (x: number, y: number, width: number, height: number) => {
            secondSectionY.current = y;
            secondSectionHeight.current = height;
          },
          () => {},
        );
      }
    };

    measureSecondSection();
    const interval = setInterval(measureSecondSection, 500);
    return () => clearInterval(interval);
  }, [secondSectionRef, scrollRef]);

  // Check visibility based on scrollY prop - show when 40% of second section is visible
  useEffect(() => {
    if (scrollY > 0) {
      hasScrolled.current = true;
    }

    if (
      !hasScrolled.current ||
      scrollY <= 0 ||
      secondSectionHeight.current <= 0
    ) {
      if (hasReachedThreshold.current) {
        hasReachedThreshold.current = false;
        opacity.value = 0;
      }
      return;
    }

    const threshold =
      secondSectionY.current + secondSectionHeight.current * 0.4;
    const shouldShowButton = scrollY >= threshold;

    if (shouldShowButton && !hasReachedThreshold.current) {
      hasReachedThreshold.current = true;
      opacity.value = withTiming(1, { duration: 200 });
    } else if (!shouldShowButton && hasReachedThreshold.current) {
      hasReachedThreshold.current = false;
      opacity.value = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollY, opacity]);

  const animatedStyle = useAnimatedStyle(
    () => ({
      opacity: opacity.value,
      pointerEvents: opacity.value > 0 ? "auto" : "none",
    }),
    [opacity],
  );

  // Don't render at all until threshold is reached to prevent flickering
  if (!hasReachedThreshold.current) {
    return null;
  }

  return (
    <Animated.View
      entering={FadeIn.duration(200)}
      exiting={FadeOut.duration(200)}
      style={[
        {
          position: "absolute",
          bottom: getTokenValue("$300"),
          left: "50%",
          transform: [{ translateX: "-50%" }],
          zIndex: STICKY_BUTTON_Z_INDEX,
          width: "100%",
          maxWidth: getTokenValue("$constraint"),
          paddingHorizontal: getTokenValue("$screenPadding"),
        },
        animatedStyle,
      ]}
    >
      <XStack justifyContent="center">
        <Button
          mode="primary"
          icon={MessagesSquare}
          onPress={() => {
            if (scrollRef.current) {
              scrollTo(scrollRef, 0, 0, true);
              // Delay to ensure scroll completes before focusing
              setTimeout(() => {
                chatInputRef?.current?.focus();
              }, 300);
            } else {
              chatInputRef?.current?.focus();
            }
          }}
          dataTrackingId="sticky-chat-button"
          shadowColor="$shadowColor"
        >
          Chat for free
        </Button>
      </XStack>
    </Animated.View>
  );
}
